import { useMemo, useRef, useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import 'antd/dist/antd.min.css';

import TranslateIntlProvider from 'translations/TranslateIntlProvider';
import MessageContextProvider from 'utils/context/MessagesContext';
import { ReactKeycloakProvider, useKeycloak } from './auth/KeycloakProvider';
import BugsnagWrapper from './components/Bugsnag/BugsnagWrapper';
import HotjarProvider from './components/HotjarProvider';
import MaintenanceMode from './components/MaintenanceMode';
import { createApolloClient } from './graphql/client';
import keycloak from './keycloak';
import Fallback from './router/Fallback';
import MainRouter from './router/MainRouter';
import './styles/index.scss';

// Component that creates Apollo client with Keycloak context
const ApolloClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { keycloak: keycloakInstance } = useKeycloak();
  const keycloakRef = useRef(keycloakInstance);

  // Keep the ref updated
  useEffect(() => {
    keycloakRef.current = keycloakInstance;
  }, [keycloakInstance]);

  const apolloClient = useMemo(() => {
    const getKeycloakToken = () => {
      return keycloakRef.current?.token;
    };

    return createApolloClient(getKeycloakToken);
  }, []); // Create once and never recreate

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

const App: React.FC = () => {
  const maintenanceModeIsOn = false;
  const productionDomain = 'app.memoweb.be';

  // Explicitly set redirectUri to ensure Keycloak can redirect back
  // Use origin + pathname to match exactly what Keycloak expects
  const redirectUri = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;

  return (
    <BugsnagWrapper>
      <ReactKeycloakProvider
        authClient={keycloak}
        autoRefreshToken
        LoadingComponent={<Fallback />}
        initOptions={{
          onLoad: 'login-required',
          // onLoad: 'check-sso',
          adapter: 'default',
          flow: 'implicit',
          checkLoginIframe: false,
          redirectUri: redirectUri,
        }}
        onEvent={(event, error) => {
          if (
            process.env.NODE_ENV === 'development' ||
            window.location.hostname.includes('acc.app.offiweb.be')
          ) {
            console.log('[Keycloak] Event:', event, error);
            if (event === 'onAuthSuccess') {
              console.log('[Keycloak] Auth success - redirectUri:', window.location.origin);
              console.log(
                '[Keycloak] Token after success:',
                keycloak.token ? 'present' : 'missing',
              );
            }
          }

          if (event === 'onAuthError' && error) {
            console.error('[Keycloak] Authentication error:', error);
          }

          if (event === 'onInitError' && error) {
            console.error('[Keycloak] Init error:', error);
          }

          if (event === 'onReady' && !error) {
            console.log('[Keycloak] onReady - authenticated:', keycloak.authenticated);
            console.log('[Keycloak] Current URL:', window.location.href);
            console.log('[Keycloak] Token:', keycloak.token ? 'present' : 'missing');
            console.log('[Keycloak] RedirectUri:', redirectUri);

            // Check if we have a token in the URL hash (after Keycloak redirect)
            // If authenticated but URL still has hash fragments, Keycloak might not have processed them yet
            const hasHashFragments = window.location.hash && window.location.hash.length > 1;
            if (keycloak.authenticated && hasHashFragments) {
              if (
                process.env.NODE_ENV === 'development' ||
                window.location.hostname.includes('acc.app.offiweb.be')
              ) {
                console.log('[Keycloak] Token found in URL hash, Keycloak should process it');
              }
            }

            // Load user info and profile asynchronously - don't block on these
            // They're optional and may fail due to network/CORS issues
            keycloak.loadUserInfo().catch((err) => {
              if (
                process.env.NODE_ENV === 'development' ||
                window.location.hostname.includes('acc.app.offiweb.be')
              ) {
                console.warn('[Keycloak] Failed to load userInfo:', err);
              }
            });
            keycloak.loadUserProfile().catch((err) => {
              if (
                process.env.NODE_ENV === 'development' ||
                window.location.hostname.includes('acc.app.offiweb.be')
              ) {
                console.warn('[Keycloak] Failed to load profile:', err);
              }
            });
          }
        }}
      >
        <ApolloClientProvider>
          <TranslateIntlProvider>
            <HotjarProvider>
              {maintenanceModeIsOn && window.location.hostname.startsWith(productionDomain) ? (
                <MaintenanceMode />
              ) : (
                <MessageContextProvider>
                  <MainRouter />
                </MessageContextProvider>
              )}
            </HotjarProvider>
          </TranslateIntlProvider>
        </ApolloClientProvider>
      </ReactKeycloakProvider>
    </BugsnagWrapper>
  );
};

export default App;
